'use client';

import {
    type ChangeEvent,
    type ComponentProps,
    type FormEvent,
    type MouseEvent,
    memo,
    type PointerEvent,
    useCallback,
    useMemo,
} from 'react';
import { InstantSearchNext } from 'react-instantsearch-nextjs';

import type { SFCCFilterMap } from '@libs/algolia';
import {
    AlgoliaIndice,
    createAlgoliaClient,
    FILTERS,
    getAlgoliaInsightsInitOptions,
    ORDERED_FILTERS,
} from '@libs/algolia';
import { getGaUserId, isOptedOutTracking, TrackingParams } from '@libs/analytics';
import { useEffectOnce, useSearchPage } from '@libs/common-next';

import ConfigureSearch from '@/components/ConfigureSearch/ConfigureSearch';
import ProductList from '@/components/ProductList/ProductList';
import type { Facets } from '@/types/facetedNavigation';
import type { SeoMetadata } from '@/types/seo';
import type { SubcatFiltering } from '@/utils/category-configuration-filters';

import { hasValidUserToken } from './algoliaRequestUtils';
import { useInstantSearchSessionManager } from './useInstantSearchSessionManager';
import {
    VirtualNumericMenu,
    VirtualPagination,
    VirtualRangeInput,
    VirtualRefinementList,
    VirtualSearchBox,
    VirtualSortBy,
} from './utils';

export type SearchLayoutProps = Readonly<{
    entryId?: string;
    preFilters: string[];
    preFilterLogic: string;
    forceCategoryH1?: boolean;
    urlFilters: SFCCFilterMap[] | Facets[];
    algoliaCategoryPath: string;
    seoMetadata: SeoMetadata;
    isCategoryActive: boolean;
    previewDate: number;
    subcatFiltering?: SubcatFiltering;
}>;

export type FacetHitProps = {
    value: string;
};

export type FacetProps = {
    facetHits: FacetHitProps[];
};

type SearchInteractionEvent =
    | ChangeEvent<HTMLDivElement>
    | FormEvent<HTMLDivElement>
    | MouseEvent<HTMLDivElement>
    | PointerEvent<HTMLDivElement>;

type InstantSearchInsightsOptions = Exclude<NonNullable<ComponentProps<typeof InstantSearchNext>['insights']>, boolean>;

const SEARCH_INTERACTION_SELECTOR = '[data-search-interaction="true"], .drawer-filter';
let viewedEventTimeout: ReturnType<typeof setTimeout> | undefined;
const VIEWED_EVENT_DEBOUNCE_MS = 250;

const isSearchInteractionTarget = (target: EventTarget | null) =>
    target instanceof Element && Boolean(target.closest(SEARCH_INTERACTION_SELECTOR));

const getReadyInsightsUserToken = (): string | undefined => {
    if (globalThis.window === undefined || isOptedOutTracking()) {
        return undefined;
    }

    const userToken = getGaUserId('_ga');
    return hasValidUserToken(userToken) ? userToken : undefined;
};

const ComponentRefinementList = () =>
    ORDERED_FILTERS.filter(f => !f.isNumeric).map(f => (
        <VirtualRefinementList key={`${f.attribute}-${f.urlName}`} attribute={f.attribute} />
    ));

function SearchLayout({
    entryId,
    preFilters,
    preFilterLogic,
    forceCategoryH1,
    urlFilters,
    algoliaCategoryPath,
    seoMetadata,
    isCategoryActive,
    previewDate,
    subcatFiltering = {},
}: SearchLayoutProps) {
    const isSearchPage = useSearchPage();
    const { markUserInteractedWithSearch, onStateChange, routing, searchClient } = useInstantSearchSessionManager({
        isSearchPage,
        urlFilters,
    });

    // Keep the Insights middleware mounted and initialized, but forward events
    // only while consent and a real GA token are present. `onEvent` replaces
    // InstantSearch's sender, so viewed events are coalesced to avoid sending the
    // transient startup batch before the settled PLP results.
    const insightsProps = useMemo<InstantSearchInsightsOptions>(
        () => ({
            insightsInitParams: getAlgoliaInsightsInitOptions(),
            onEvent(event, insightsClient) {
                const insightsMethod = event.insightsMethod;
                const userToken = getReadyInsightsUserToken();
                if (!insightsMethod || !userToken) {
                    return;
                }

                const getPayload = () => ({
                    ...event.payload,
                    userToken: getReadyInsightsUserToken(),
                    algoliaSource:
                        event.eventModifier === 'internal'
                            ? ['instantsearch', 'instantsearch-internal']
                            : ['instantsearch'],
                });

                if (insightsMethod === 'viewedObjectIDs') {
                    const viewedPayload = getPayload();
                    if (!viewedPayload.userToken) return;

                    clearTimeout(viewedEventTimeout);
                    viewedEventTimeout = setTimeout(() => {
                        if (getReadyInsightsUserToken()) {
                            insightsClient?.(insightsMethod, viewedPayload as never);
                        }
                    }, VIEWED_EVENT_DEBOUNCE_MS);
                    return;
                }

                insightsClient?.(insightsMethod, getPayload() as never);
            },
        }),
        []
    );

    const handleSearchInteraction = useCallback(
        (event: SearchInteractionEvent) => {
            if (isSearchInteractionTarget(event.target)) {
                markUserInteractedWithSearch();
            }
        },
        [markUserInteractedWithSearch]
    );

    const handlePointerDownCapture = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
            if (event.pointerType === 'mouse') {
                handleSearchInteraction(event);
            }
        },
        [handleSearchInteraction]
    );

    useEffectOnce(() => {
        const isRedirectedFromSearch = Boolean(window.sessionStorage.getItem('searchRedirectTerm'));
        window.sessionStorage.removeItem('searchRedirectTerm');

        window.sessionStorage.pdpFindingMethod =
            isSearchPage || isRedirectedFromSearch ? TrackingParams.SEARCH_REFERER : TrackingParams.BROWSE_REFERER;

        const handleFacets = (data: Event) => {
            const client = createAlgoliaClient({ caller: 'web:plp:searchForFacetValues-client' });
            const index = client.initIndex(AlgoliaIndice.PRODUCTS);
            const eventData = data as CustomEvent;
            const { facetName, facetKeys, variant } = eventData.detail;

            const indices = facetKeys.map((key: string) =>
                index.searchForFacetValues(facetName, key, {
                    maxFacetHits: 100,
                })
            );

            const getFacetResults = (facetData: FacetProps[]) => {
                const hits: FacetHitProps[] = [];

                facetData.forEach((facet: FacetProps) =>
                    facet.facetHits.forEach((facetHit: FacetHitProps) =>
                        facetKeys?.forEach((key: string) => (facetHit.value === key ? hits.push(facetHit) : null))
                    )
                );

                window.dispatchEvent(
                    new CustomEvent('algoliaDisjunctiveFacets', {
                        detail: {
                            variant,
                            hits,
                        },
                    })
                );
            };

            Promise.all(indices).then(facetData => getFacetResults(facetData as FacetProps[]));
        };

        window.addEventListener('getAlgoliaDisjunctiveFacets', handleFacets);

        return () => {
            window.removeEventListener('getAlgoliaDisjunctiveFacets', handleFacets);
        };
    });

    return (
        <div
            className="instant-search-container"
            onChangeCapture={handleSearchInteraction}
            onClickCapture={handleSearchInteraction}
            onInputCapture={handleSearchInteraction}
            onPointerDownCapture={handlePointerDownCapture}
        >
            <InstantSearchNext
                searchClient={searchClient}
                indexName={AlgoliaIndice.PRODUCTS}
                onStateChange={onStateChange}
                routing={routing}
                future={{ preserveSharedStateOnUnmount: true }}
                insights={insightsProps}
            >
                <ComponentRefinementList />
                <VirtualPagination />
                <VirtualNumericMenu attribute={FILTERS.RATING.ATTRIBUTE} items={FILTERS.RATING.ITEMS} />
                <VirtualSortBy isSearchPage />
                <VirtualRangeInput attribute={FILTERS.RANGE.ATTRIBUTE} />
                <VirtualSearchBox />
                <ConfigureSearch
                    preFilters={preFilters}
                    preFilterLogic={preFilterLogic}
                    algoliaCategoryPath={algoliaCategoryPath}
                    previewDate={previewDate}
                    subcatFiltering={subcatFiltering}
                />
                <ProductList
                    entryId={entryId}
                    forceCategoryH1={forceCategoryH1}
                    seoMetadata={seoMetadata}
                    isCategoryActive={isCategoryActive}
                />
            </InstantSearchNext>
        </div>
    );
}

export default memo(SearchLayout);
